import { MaximizeIcon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetSummary } from "~/api/chat/chat";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { FancyLoader } from "~/components/FancyLoader";
import { Loader } from "~/components/Loader";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TTSButton } from "~/components/TTSButton";
import { Button } from "~/components/ui/button";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatCaseInfo } from "~/features/chat/helpers";
import { useSummaryContent } from "~/features/chat/hooks/useSummaryContent";
import { useStoredSummary } from "~/features/chat/hooks/useSummaryStore";
import { useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { shortRelativeTime } from "~/utils/date";
import { htmlToMarkdown } from "~/utils/htmlToMarkdown";
import { useNote } from "../hooks/useNote";
import { AudioRecordingButton } from "./AudioRecordingButton";
import { NoteEditor } from "./NoteEditor";

interface NotesAreaProps {
  className?: string;
  isActiveTab?: boolean;
}

export const NotesArea: FC<NotesAreaProps> = ({
  className,
  isActiveTab = true,
}) => {
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [activeLen, setActiveLen] = useState<number | null>(null);
  const [lastSpokenText, setLastSpokenText] = useState<string>("");
  const [spokenBeforeImprovement, setSpokenBeforeImprovement] =
    useState<string>("");
  const { chatId, organizationId, caseId, isViewOnly } =
    useChatDocumentParams();
  const { isMobile } = useMobile();

  const { project } = useParentCase();
  const caseInfo = project ? formatCaseInfo(project) : undefined;

  const summaryData = useStoredSummary(chatId, "summary");
  const { data: fetchedSummary } = useGetSummary(
    organizationId,
    caseId,
    chatId,
    {
      type: "summary",
    },
    {
      query: {
        staleTime: Infinity,
        enabled: !summaryData?.content,
      },
    }
  );

  const { data: summaryContent } = useSummaryContent(
    fetchedSummary?.contentUrl
  );

  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    note,
    noteContent,
    isFetching,
    isSaving,
    isProcessingImprovement,
    isProcessingQueue,
    handleContentChange,
    handleAutoImproveChunk,
    handleNoteImprove,
  } = useNote();

  const exportFileName = `Case Notes ${formatMessage({ id: "common.case" })}`;

  const onAutoImproveChunk = async (chunk: string) => {
    if (!spokenBeforeImprovement.length) {
      setSpokenBeforeImprovement(chunk);
    }
    setLastSpokenText("");

    const improvedNote = await handleAutoImproveChunk(chunk);

    setSpokenBeforeImprovement("");

    if (improvedNote) {
      await handleContentChange(improvedNote);
    }
  };

  const onSpeechUpdate = (text: string) => {
    setLastSpokenText(text);
  };

  const isContentEmpty =
    !noteContent ||
    noteContent.trim() === "" ||
    htmlToMarkdown(noteContent).trim() === "";

  const onNoteImprove = (): Promise<void> => {
    const fullSummaryContent = summaryData?.content || summaryContent || "";
    setSpokenBeforeImprovement("");
    setLastSpokenText("");
    return handleNoteImprove(fullSummaryContent);
  };

  return (
    <>
      <div
        className={cn(
          "relative",
          isMobile ? "flex flex-col h-docsidebar-content-mobile" : "",
          className
        )}
      >
        <div
          className={cn(
            "border-zinc-100",
            isMobile
              ? "flex-1 min-h-0"
              : isActiveTab && note
                ? "h-[calc(100vh-116px)]"
                : "h-[calc(100vh-92px)]"
          )}
        >
          {isFetching && (
            <div className="flex items-center justify-center h-full">
              <Loader />
            </div>
          )}
          {isProcessingImprovement && (
            <div className="flex items-center justify-center absolute left-1/2 bottom-1/2 -translate-x-1/2 -translate-y-1/2">
              <FancyLoader />
            </div>
          )}
          {!isFetching && !isProcessingImprovement && (
            <NoteEditor
              content={noteContent + spokenBeforeImprovement + lastSpokenText}
              onChange={handleContentChange}
              readOnly={isRecordingActive || isViewOnly}
              placeholder={formatMessage({
                id: "notes.placeholder",
                defaultMessage:
                  "Leave helpful notes here, they are visible org-wide.",
              })}
              className="h-full min-h-[400px] overflow-y-auto overflow-x-hidden"
              activeTtsIdx={isReading ? activeIdx : null}
              activeTtsLen={isReading ? activeLen : null}
            />
          )}
        </div>

        {note && (
          <div
            className={`w-full h-6 px-6 items-center flex border-t border-border bg-background justify-center ${isMobile ? "flex-shrink-0" : ""}`}
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {isSaving ? (
                <span>Saving...</span>
              ) : (
                <>
                  {(() => {
                    const timeAgo = shortRelativeTime(
                      note.updatedAt,
                      intl.locale
                    );
                    const isJustNow =
                      timeAgo === "just now" || timeAgo === "à l'instant";
                    return isJustNow ? (
                      <FormattedMessage
                        id="notes.status.editedJustNowBy"
                        defaultMessage="Edited just now by"
                      />
                    ) : (
                      <FormattedMessage
                        id="notes.status.editedAgoBy"
                        defaultMessage="Edited {timeAgo} ago by"
                        values={{ timeAgo }}
                      />
                    );
                  })()}
                  <span> {note.author.split("@")[0]}</span>
                </>
              )}
            </div>
          </div>
        )}
        {!isViewOnly && (
          <AudioRecordingButton
            onRecordingStateChange={setIsRecordingActive}
            isProcessingImprovement={isProcessingImprovement}
            isLoading={isSaving || isFetching}
            onAutoImproveChunk={onAutoImproveChunk}
            onRecordingStop={onNoteImprove}
            isProcessingQueue={isProcessingQueue}
            onTextSpeechUpdate={onSpeechUpdate}
            className={`${!note && !isFetching ? "bottom-[42px]" : "bottom-[65px]"}`}
          />
        )}
        <div
          className={`w-full h-8 px-6 items-center flex border-t border-border justify-center ${isMobile ? "flex-shrink-0" : ""}`}
        >
          <div className="flex gap-1 xl:gap-2">
            <TTSButton
              text={htmlToMarkdown(noteContent)}
              isLoading={isSaving || isFetching}
              onStart={() => setIsReading(true)}
              onEnd={() => {
                setIsReading(false);
                setActiveIdx(null);
                setActiveLen(null);
              }}
              onBoundary={({ charIndex, charLength }) => {
                setActiveIdx(charIndex);
                setActiveLen(typeof charLength === "number" ? charLength : 0);
              }}
            />
            <Button
              variant="ghost"
              size="mini"
              disabled={isContentEmpty}
              onClick={() => {
                trackEvent({ eventName: "app/notes/maximize.button/click" });
                setMaximizeModalOpen(true);
              }}
            >
              <MaximizeIcon className="w-4 h-4 mr-1" />
              <FormattedMessage
                id="common.maximize"
                defaultMessage="Maximize"
              />
            </Button>
            <ContentCopyButton
              content={noteContent}
              size="mini"
              preCopyMessageId="copy"
              eventName="app/notes/copy_all.button/click"
              transformContent={htmlToMarkdown}
              disabled={isContentEmpty}
            />
            <MarkdownExportButton
              content={noteContent}
              fileName={exportFileName}
              buttonSize="mini"
              openEventName="app/notes/export.button/click"
              exportEventName="app/notes/export_menu_open/export_action.link/click"
              transformContent={htmlToMarkdown}
              disabled={isContentEmpty}
              title={formatMessage({
                id: "common.notes",
              })}
              caseInfo={caseInfo}
            />
          </div>
        </div>
      </div>

      {maximizeModalOpen && (
        <MarkdownContentModal
          title="Case Notes"
          content={noteContent}
          open={maximizeModalOpen}
          onOpenChange={setMaximizeModalOpen}
          copyEventName="app/notes_maximize_modal/copy_all.button/click"
          exportOpenEventName="app/notes_maximize_modal/export.button/click"
          exportEventName="app/notes_maximize_modal/export_menu_open/export_action.link/click"
          terms={[]}
          caseInfo={caseInfo}
        />
      )}
    </>
  );
};
